import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./usersList.module.css";
import React, { useEffect, useMemo, useState } from "react";
import Modal from "../modals/modal/Modal";
import PrivacyProtectionPopup from "../modals/privacyProtectionPopup/privacyProtectionPopup";
import { IUserReport } from "../../services/types/users.types";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import DeleteConfirmation from "../modals/deleteConfirmation/deleteConfirmation";
import PermanentDeleteConfirmation from "../modals/permanentDeleteConfirmation/permanentDeleteConfirmation";
import {
  usePermanentDeleteClientUserMutation,
  useUpdateUserStatusMutation,
} from "../../services/api/user.api";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import { isSuperAdminByRoleId } from "../../services/selectors/admin.selectors";
import TreatmentPlanPopup from "../modals/treatmentPlanPopup/treatmentPlanPopup";
import ListPagination from "../listPagination/listPagination";
import { TLimits } from "../../types/types";
import ActionButtonTooltip from "../ActionButtonTooltip/actionButtonTooltip";
import editProfileIcon from "../../images/edit_profile_icon.png";
import deleteIcon from "../../images/delete_icon.svg";
import StatusSwitchToggle from "../statusSwitchToggle/statusSwitchToggle";
import { useActions } from "../../services/hooks/useActions";
import { currentUserSelector } from "../../services/selectors/users.selectors";
import { useSearchParams } from "react-router-dom";

function UsersListStaff({
  userList,
  openEditUserForm,
  shouldResetPagination,
  setShouldResetLimit,
  refetchUsersTable,
}: {
  userList: IUserReport[];
  openEditUserForm: (user: IUserReport, initialTab?: 'payments' | 'therapists') => void;
  shouldResetPagination: boolean;
  setShouldResetLimit: (bool: boolean) => void;
  refetchUsersTable?: () => void;
}) {
  const admin = useAppSelector(adminSelector);
  const isSuperAdmin = useAppSelector(isSuperAdminByRoleId);

  const [user, setUser] = useState<IUserReport | undefined>(undefined);
  const [pendingAction, setPendingAction] = useState<"none" | "permanentDelete">(
    "none"
  );
  /** Status the toggle is about to apply (1 = activate, 0 = deactivate). */
  const [pendingStatus, setPendingStatus] = useState<number | null>(null);
  const [openPrivacyPopup, setOpenPrivacyPopup] = useState(false);
  const [openTreatmentPlanPopup, setOpenTreatmentPlanPopup] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openPermanentDeleteConfirmation, setOpenPermanentDeleteConfirmation] =
    useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openErrorModal, setOpenErrorModal] = useState<string | undefined>(
    undefined
  );

  const [updateUserStatus] = useUpdateUserStatusMutation();
  const [permanentDeleteClientUser] = usePermanentDeleteClientUserMutation();

  const handlePermanentDeleteUser = ({
    deleteClinicalData,
  }: {
    deleteClinicalData: boolean;
  }) => {
    if (!user) return;
    const { Password, Roles, Id, ...rest } = user.Users;

    permanentDeleteClientUser({
      companyId: admin.companyId,
      // @ts-ignore — server expects same PascalCase user payload as DML/2
      body: { ...rest, deleteClinicalData },
    }).then((res) => {
      if ("data" in res && res.data) {
        if ("desc" in res.data && typeof res.data.desc === "string") {
          setOpenErrorModal(res.data.desc);
        } else {
          setOpenSuccessModal(true);
        }
      } else if ("error" in res) {
        setOpenErrorModal(t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"));
      }
    });
  };

  const handleOpenTreatmentPlanPopup = () => {
    setOpenTreatmentPlanPopup(true);
  };

  const therapistStep = 6;

  //params to store current page index of the table
  const [searchParams, setSearchParams] = useSearchParams();
  const page = searchParams.get("page");
  const pageAsNumber = Number(page);

  const [therapistLimits, setTherapistLimits] = useState<TLimits>(
    pageAsNumber
      ? {
        start: (pageAsNumber - 1) * therapistStep + 1,
        end: pageAsNumber * therapistStep,
      }
      : { start: 1, end: therapistStep }
  );

  useEffect(() => {
    // reset pagination on each input change
    if (shouldResetPagination) {
      setTherapistLimits({ start: 1, end: Math.max(therapistStep, 1) });
      setShouldResetLimit(false);
    } else if (
      pageAsNumber * therapistStep > userList.length &&
      userList.length > 0
    )
      setTherapistLimits({
        start:
          Math.floor((userList.length - 1) / therapistStep) * therapistStep + 1,
        end: userList.length,
      });
  }, [userList]);

  // Update URL search params when therapistLimits change
  useEffect(() => {
    setSearchParams({
      page: (Math.floor(therapistLimits.start / therapistStep) + 1).toString(),
    });
  }, [therapistLimits]);

  const therapistList = useMemo(
    () => {
      return userList.slice(therapistLimits.start - 1, therapistLimits.end);
    },
    [userList, therapistLimits]
  );

  // Status changes go through the dedicated PATCH user/{guid}/status endpoint — it carries the
  // status and nothing else, and the fulfilled response patches the row in the users slice.
  // `target` is passed when the row is toggled directly (activation), since the `user` state
  // is not committed yet at that point.
  const handleChangeUserStatus = (newStatus: number, target?: IUserReport) => {
    const guid = (target ?? user)?.Users.Guid;
    if (!guid) {
      setOpenErrorModal(t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER"));
      return;
    }

    updateUserStatus({ guid: String(guid), statusId: newStatus }).then((res) => {
      if ("data" in res && res.data?.success) {
        setOpenSuccessModal(true);
      } else if ("data" in res && res.data === null) {
        // Plan/quota rejection — base-query already showed the restriction popup.
      } else {
        const desc = (res as any)?.error?.data?.error;
        setOpenErrorModal(
          typeof desc === "string"
            ? desc
            : t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER")
        );
      }
    });
  };
  const proceed = () => {
    if (pendingAction === "permanentDelete" && user) {
      setOpenPermanentDeleteConfirmation(true);
      setPendingAction("none");
      setOpenPrivacyPopup(false);
      return;
    }

    user && openEditUserForm && openEditUserForm(user);
  };

  //highlight user
  const { setCurrentUser } = useActions();
  const selectUser = (id: number) => () => {
    setCurrentUser(id);
  };
  const currentUser = useAppSelector(currentUserSelector);

  return (
    <>
      <article className={`section ${styles.section}`}>
        <h3 className="sectionTitle">{t('USER_THERAPISTS')}</h3>
        <ul className="list">
          <li className={`listRow listRow_header  ${styles.listRowTherapist}`}>
            <p className={`listCell`}>{t("COLUMN_ID")}</p>
            <p className={`listCell`}>{t("ROLE")}</p>
            <p className={`listCell`}>{t('NAME_2')}</p>
            <p className={`listCell`}>{t("PHONE")}</p>
            <p className={`listCell`}>{t("EMAIL")}</p>
            <p className={`listCell`}>{t("AVAILABLE_HOURS")}</p>
            <p className={`listCell`}>{t("TREATMENT_PLANS_COUNT")}</p>
            <p className={`listCell`}>{t("EDIT")}</p>
            <p className={`listCell`}>{t("COLUMN_STATUS")}</p>
          </li>

          {therapistList.map((u: IUserReport, index: number) => (
            <li
              key={index}
              data-testid={`users-therapist-row-${index}`}
              className={`listRow ${index % 2 === 0 && "colored"} ${styles.listRowTherapist
                } ${u.Users.Id === currentUser ? "listRow_selected" : ""}`}
              onClick={selectUser(u.Users.Id)}
            >
              <p className={`listCell `}>{u.Users.Id}</p>
              <p className={`listCell `}>{u.RoleName || ""}</p>
              <p className={`listCell `} data-testid="therapist-name">
                {u.Users.FullName ? (
                  u.Users.FullName
                ) : (
                  <>
                    {u.Users.FirstName} {u.Users.LastName || ""}
                  </>
                )}
              </p>
              <p className={`listCell `}>{u.Users.Phone || "-"}</p>
              <p className={`listCell `}>{u.Users.Email || "-"}</p>
              <p className={`listCell `}>
                {Math.floor(u.AvailabilityHours ?? 0) || "-"}
              </p>
              <p
                className={`listCell `}
                onClick={() => {
                  if (u.AppointmentsProgramsNum) {
                    setUser(u);
                    handleOpenTreatmentPlanPopup();
                  }
                }}
                style={u.AppointmentsProgramsNum ? { cursor: "pointer" } : {}}
              >
                {u.AppointmentsProgramsNum || "-"}
              </p>
              <div className="listCell edit_icon">
                <ActionButtonTooltip
                  data-testid="edit-therapist-button"
                  className="btn"
                  onClick={() => {
                    setUser(u);
                    setOpenPrivacyPopup(true);
                  }}
                  text={t('EDIT_USER')}
                  tooltipPosition="center"
                >
                  <img
                    src={editProfileIcon}
                    alt={t('EDIT_1')}
                    className="editIconImage"
                  />
                </ActionButtonTooltip>
              </div>
              <div className={`listCell ${styles.statusCell}`} onClick={(e) => e.stopPropagation()}>
                <span className={styles.statusCellToggleWrap}>
                  <StatusSwitchToggle
                    isActive={u.Users.StatusId === 1}
                    onToggle={() => {
                      // No privacy declaration — a status change exposes no clinical data.
                      // Deactivation asks for confirmation; activation is harmless and
                      // reversible, so it applies straight away.
                      setUser(u);
                      if (u.Users.StatusId === 1) {
                        setPendingStatus(0);
                        setOpenDeleteConfirmation(true);
                      } else {
                        handleChangeUserStatus(1, u);
                      }
                    }}
                  />
                </span>
                {isSuperAdmin && (
                  <>
                    <span className={styles.statusCellDivider} aria-hidden />
                    <span
                      className={styles.permanentDeleteWrap}
                      data-testid="permanent-delete-staff-button"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ActionButtonTooltip
                        className={styles.btn_permanentDelete}
                        onClick={() => {
                          setUser(u);
                          setPendingAction("permanentDelete");
                          setOpenPrivacyPopup(true);
                        }}
                        text={t("PERMANENT_DELETE_USER_TOOLTIP")}
                        tooltipPosition="center"
                      >
                        <img
                          src={deleteIcon}
                          alt=""
                          className={styles.permanentDeleteIcon}
                        />
                      </ActionButtonTooltip>
                    </span>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </article>
      <ListPagination
        limits={therapistLimits}
        total={userList.length}
        step={therapistStep}
        setLimits={setTherapistLimits}
      />
      {openPermanentDeleteConfirmation && user && (
        <Modal
          wide
          onClose={() => {
            setOpenPermanentDeleteConfirmation(false);
            setUser(undefined);
          }}
        >
          <PermanentDeleteConfirmation
            user={user}
            onDelete={handlePermanentDeleteUser}
            onClose={() => {
              setOpenPermanentDeleteConfirmation(false);
              setUser(undefined);
            }}
          />
        </Modal>
      )}
      {openDeleteConfirmation && pendingStatus !== null && (
        <Modal
          onClose={() => {
            setOpenDeleteConfirmation(false);
            setPendingStatus(null);
          }}
        >
          <DeleteConfirmation
            onDelete={() => handleChangeUserStatus(pendingStatus)}
            text={t('ARE_YOU_SURE_TO_CHANGE_STATUS', { firstName: user?.Users?.FirstName })}
            onClose={() => {
              setOpenDeleteConfirmation(false);
              setPendingStatus(null);
            }}
          />
        </Modal>
      )}
      {openPrivacyPopup && user && (
        <Modal
          onClose={() => {
            setOpenPrivacyPopup(false);
            setOpenDeleteConfirmation(false);
            setOpenPermanentDeleteConfirmation(false);
            setPendingAction("none");
            setUser(undefined);
          }}
        >
          <PrivacyProtectionPopup
            onClose={proceed}
            clientUsersGuid={user?.Users.Guid || ""}
          />
        </Modal>
      )}

      {openTreatmentPlanPopup && user && (
        <Modal
          onClose={() => {
            setOpenTreatmentPlanPopup(false);
          }}
          wide
        >
          <TreatmentPlanPopup
            onClose={() => {
              setOpenTreatmentPlanPopup(false);
            }}
            user={user}
          />
        </Modal>
      )}
      {openSuccessModal && (
        <Modal
          onClose={() => {
            setOpenSuccessModal(false);
            refetchUsersTable?.();
          }}
        >
          <GeneralSuccessPopup
            onClose={() => {
              setOpenSuccessModal(false);
              refetchUsersTable?.();
            }}
          />
        </Modal>
      )}
      {openErrorModal && (
        <Modal
          onClose={() => {
            setOpenErrorModal(undefined);
          }}
        >
          <ErrorPopup
            text={openErrorModal}
            onClose={() => {
              setOpenErrorModal(undefined);
            }}
          />
        </Modal>
      )}
    </>
  );
}

export default UsersListStaff;
